/**
 * Export do banco em um arquivo .sqlite portátil (backup / levar ao profissional /
 * futuro import). O banco VIVO continua sendo o IndexedDB (Dexie) — isto é só uma
 * fotografia em SQLite gerada sob demanda, 100% client-side (sql.js/WASM), sem
 * servidor. Roda igual na web e no WebView nativo. Export é ação explícita (P0.8).
 *
 * O schema embutido é o mesmo de src/schema.sql (fonte única, importado como texto).
 */
import initSqlJs from "sql.js";
import wasmUrl from "sql.js/dist/sql-wasm.wasm?url";
import schemaSql from "../schema.sql?raw";
import { db } from "../schema.dexie";
import type { Alimento, Config, Crianca, Oferta } from "../types";
import { CONFIG_PADRAO } from "../types";
import type { ExperienciaMusical, MomentoMusical } from "../music/types";
import type { AnexoClinico, NotaSessao, Profissional } from "../elo/types";
import type { AtividadePsicomotora, MomentoPsicomotor } from "../body/types";

let sqlPromise: ReturnType<typeof initSqlJs> | null = null;
function carregarSql() {
  if (!sqlPromise) sqlPromise = initSqlJs({ locateFile: () => wasmUrl });
  return sqlPromise;
}

const bool = (v: boolean) => (v ? 1 : 0);

/** Gera o arquivo .sqlite (bytes) com todo o conteúdo do banco local. */
export async function gerarSqlite(): Promise<Uint8Array> {
  const SQL = await carregarSql();
  const sdb = new SQL.Database();
  try {
    sdb.run(schemaSql); // cria tabelas + a linha config(id=1) com defaults

    const [criancas, alimentos, ofertas, fotos, cfg, experiencias, momentos, profissionais, notas, atividades, momentosPsm] =
      await Promise.all([
        db.crianca.toArray(),
        db.alimento.toArray(),
        db.oferta.toArray(),
        db.foto.toArray(),
        db.config.get("singleton"),
        db.experiencia.toArray(),
        db.momento.toArray(),
        db.profissional.toArray(),
        db.nota.toArray(),
        db.atividade.toArray(),
        db.momento_psm.toArray(),
      ]);

    // ordem respeita as FKs: crianca → alimento → oferta; foto e config por último
    let st = sdb.prepare(
      `INSERT INTO crianca
        (id,apelido,nome_completo,data_nascimento,foto,sistema_caa,responsavel_nome,responsavel_cpf,recomendacoes,criado_em)
       VALUES (?,?,?,?,?,?,?,?,?,?)`,
    );
    for (const c of criancas) {
      st.run([
        c.id,
        c.apelido,
        c.nome_completo,
        c.data_nascimento,
        c.foto,
        c.sistema_caa,
        c.responsavel_nome,
        c.responsavel_cpf,
        c.recomendacoes ?? null,
        c.criado_em,
      ]);
    }
    st.free();

    st = sdb.prepare(
      `INSERT INTO alimento
        (id,nome,fotos,marca_produto,categoria,grupo,textura,temperatura,cor,forma_apresentacao,cheiro,status,observacoes,arquivado,criado_em)
       VALUES (?,?,?,?,?,?,?,?,?,?,?,?,?,?,?)`,
    );
    for (const a of alimentos) {
      st.run([
        a.id,
        a.nome,
        JSON.stringify(a.fotos ?? []),
        a.marca_produto,
        a.categoria,
        a.grupo ?? null,
        a.sensorial.textura,
        a.sensorial.temperatura,
        a.sensorial.cor,
        a.sensorial.forma_apresentacao,
        a.sensorial.cheiro,
        a.status,
        a.observacoes,
        bool(a.arquivado),
        a.criado_em,
      ]);
    }
    st.free();

    st = sdb.prepare(
      `INSERT INTO oferta
        (id,alimento_id,crianca_id,data_hora,passo_escada,porcao_comida_pct,porcao_oferecida,refeicao,local,quem_ofereceu,quem_presente,humor_antes,origem,confirmado,notas,profissional_nome,profissional_especialidade)
       VALUES (?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?)`,
    );
    for (const o of ofertas) {
      st.run([
        o.id,
        o.alimento_id,
        o.crianca_id,
        o.data_hora,
        o.passo_escada,
        o.porcao_comida_pct,
        o.porcao_oferecida,
        o.refeicao,
        o.local,
        o.quem_ofereceu,
        JSON.stringify(o.quem_presente ?? []),
        o.humor_antes,
        o.origem,
        bool(o.confirmado),
        o.notas,
        o.profissional_nome ?? null,
        o.profissional_especialidade ?? null,
      ]);
    }
    st.free();

    // fotos como BLOB (backup completo, restaurável)
    st = sdb.prepare(`INSERT INTO foto (id,blob,criado_em) VALUES (?,?,?)`);
    for (const f of fotos) {
      const bytes = new Uint8Array(await f.blob.arrayBuffer());
      st.run([f.id, bytes, f.criado_em]);
    }
    st.free();

    // ── Compasso: experiências e momentos ─────────────────────────────────
    st = sdb.prepare(
      `INSERT INTO experiencia
        (id,nome,tipo,midias,funcao,andamento,intensidade,timbre,previsibilidade,familiaridade,modalidade,status,observacoes,arquivado,criado_em)
       VALUES (?,?,?,?,?,?,?,?,?,?,?,?,?,?,?)`,
    );
    for (const e of experiencias) {
      st.run([
        e.id,
        e.nome,
        e.tipo,
        JSON.stringify(e.midias ?? []),
        e.funcao,
        e.sonoro.andamento,
        e.sonoro.intensidade,
        e.sonoro.timbre,
        e.sonoro.previsibilidade,
        e.sonoro.familiaridade,
        JSON.stringify(e.modalidade ?? []),
        e.status,
        e.observacoes,
        bool(e.arquivado),
        e.criado_em,
      ]);
    }
    st.free();

    st = sdb.prepare(
      `INSERT INTO momento
        (id,experiencia_id,crianca_id,data_hora,passo_engajamento,estado_antes,estado_depois,duracao_min,contexto,com_quem,origem,confirmado,notas)
       VALUES (?,?,?,?,?,?,?,?,?,?,?,?,?)`,
    );
    for (const m of momentos) {
      st.run([
        m.id,
        m.experiencia_id,
        m.crianca_id,
        m.data_hora,
        m.passo_engajamento,
        m.estado_antes,
        m.estado_depois,
        m.duracao_min,
        m.contexto,
        JSON.stringify(m.com_quem ?? []),
        m.origem,
        bool(m.confirmado),
        m.notas,
      ]);
    }
    st.free();

    // ── Elo: profissionais e notas ────────────────────────────────────────
    st = sdb.prepare(
      `INSERT INTO profissional
        (id,nome,especialidade,registro_classe,contato,arquivado,criado_em)
       VALUES (?,?,?,?,?,?,?)`,
    );
    for (const p of profissionais) {
      st.run([
        p.id,
        p.nome,
        p.especialidade,
        p.registro_classe,
        p.contato,
        bool(p.arquivado),
        p.criado_em,
      ]);
    }
    st.free();

    st = sdb.prepare(
      `INSERT INTO nota
        (id,crianca_id,profissional_id,especialidade,data_sessao,objetivo,alimento_id,degrau_alvo,degrau_observado,atividades,resultados,orientacoes_casa,orientacao_feita_em,observacoes,anexos,oferta_id_gerada,estado,versao_de,criado_em)
       VALUES (?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?)`,
    );
    for (const n of notas) {
      st.run([
        n.id,
        n.crianca_id,
        n.profissional_id,
        n.especialidade,
        n.data_sessao,
        n.objetivo,
        n.alimento_id,
        n.degrau_alvo,
        n.degrau_observado,
        JSON.stringify(n.atividades ?? []),
        n.resultados,
        n.orientacoes_casa,
        n.orientacao_feita_em ?? null,
        n.observacoes,
        JSON.stringify(n.anexos ?? []),
        n.oferta_id_gerada,
        n.estado,
        n.versao_de,
        n.criado_em,
      ]);
    }
    st.free();

    // ── Gingado: atividades e momentos psicomotores ───────────────────────
    st = sdb.prepare(
      `INSERT INTO atividade
        (id,nome,tipo,midias,fator_alvo,sistema,intensidade,previsibilidade,demanda_relacional,status,seguranca,observacoes,arquivado,criado_em)
       VALUES (?,?,?,?,?,?,?,?,?,?,?,?,?,?)`,
    );
    for (const a of atividades) {
      st.run([
        a.id,
        a.nome,
        a.tipo,
        JSON.stringify(a.midias ?? []),
        a.psicomotor.fator_alvo,
        JSON.stringify(a.psicomotor.sistema ?? []),
        a.psicomotor.intensidade,
        a.psicomotor.previsibilidade,
        a.demanda_relacional,
        a.status,
        a.seguranca,
        a.observacoes,
        bool(a.arquivado),
        a.criado_em,
      ]);
    }
    st.free();

    st = sdb.prepare(
      `INSERT INTO momento_psm
        (id,atividade_id,crianca_id,data_hora,passo_participacao,nivel_ajuda,estado_antes,estado_depois,duracao_min,contexto,com_quem,origem,confirmado,notas)
       VALUES (?,?,?,?,?,?,?,?,?,?,?,?,?,?)`,
    );
    for (const m of momentosPsm) {
      st.run([
        m.id,
        m.atividade_id,
        m.crianca_id,
        m.data_hora,
        m.passo_participacao,
        m.nivel_ajuda,
        m.estado_antes,
        m.estado_depois,
        m.duracao_min,
        m.contexto,
        JSON.stringify(m.com_quem ?? []),
        m.origem,
        bool(m.confirmado),
        m.notas,
      ]);
    }
    st.free();

    // config: a linha id=1 já existe (do schema); atualiza com os valores reais
    if (cfg) {
      st = sdb.prepare(
        `UPDATE config
           SET crianca_ativa_id=?, reexposicao_dias=?, modo_escolha_por_semana=?, escolha_qtd_por_refeicao=?, whatsapp_destino=?, musica_rotina_dias=?, musica_escolha_qtd=?, elo_lembrete_dias=?, gingado_rotina_dias=?, gingado_escolha_qtd=?
         WHERE id=1`,
      );
      st.run([
        cfg.crianca_ativa_id ?? null,
        cfg.reexposicao_dias,
        cfg.modo_escolha_por_semana,
        JSON.stringify(cfg.escolha_qtd_por_refeicao ?? {}),
        cfg.whatsapp_destino ?? null,
        cfg.musica_rotina_dias ?? CONFIG_PADRAO.musica_rotina_dias,
        cfg.musica_escolha_qtd ?? CONFIG_PADRAO.musica_escolha_qtd,
        cfg.elo_lembrete_dias ?? CONFIG_PADRAO.elo_lembrete_dias,
        cfg.gingado_rotina_dias ?? CONFIG_PADRAO.gingado_rotina_dias,
        cfg.gingado_escolha_qtd ?? CONFIG_PADRAO.gingado_escolha_qtd,
      ]);
      st.free();
    }

    return sdb.export();
  } finally {
    sdb.close();
  }
}

type Linha = Record<string, unknown>;
const str = (v: unknown) => (v == null ? null : String(v));
const num = (v: unknown) => (v == null ? null : Number(v));
const jsonArr = (v: unknown) => {
  try {
    return v ? (JSON.parse(String(v)) as string[]) : [];
  } catch {
    return [];
  }
};

/**
 * Restaura um backup .sqlite para o banco local (Dexie). NÃO destrói o que já existe:
 * faz upsert por id (bulkPut) — em aparelho novo é restauração completa; com dados,
 * mescla. Roda 100% no aparelho. Retorna a contagem importada por tabela.
 */
export async function restaurarDeSqlite(bytes: Uint8Array): Promise<{
  alimentos: number;
  ofertas: number;
  fotos: number;
  criancas: number;
  experiencias: number;
  momentos: number;
  profissionais: number;
  notas: number;
  atividades: number;
  momentosPsm: number;
}> {
  const SQL = await carregarSql();
  const sdb = new SQL.Database(bytes);
  try {
    const ler = (sql: string): Linha[] => {
      const res = sdb.exec(sql);
      if (!res.length) return [];
      const { columns, values } = res[0];
      return values.map((row) =>
        Object.fromEntries(columns.map((c, i) => [c, row[i]])),
      );
    };
    const existe = (t: string) =>
      ler(`SELECT name FROM sqlite_master WHERE type='table' AND name='${t}'`).length > 0;

    const criancas: Crianca[] = (existe("crianca") ? ler("SELECT * FROM crianca") : []).map(
      (r) => ({
        id: String(r.id),
        apelido: String(r.apelido ?? "paciente"),
        nome_completo: str(r.nome_completo),
        data_nascimento: str(r.data_nascimento),
        foto: str(r.foto),
        sistema_caa: (str(r.sistema_caa) ?? "nenhum") as Crianca["sistema_caa"],
        responsavel_nome: str(r.responsavel_nome),
        responsavel_cpf: str(r.responsavel_cpf),
        recomendacoes: str(r.recomendacoes),
        criado_em: String(r.criado_em),
      }),
    );

    const alimentos: Alimento[] = (
      existe("alimento") ? ler("SELECT * FROM alimento") : []
    ).map((r) => ({
      id: String(r.id),
      nome: String(r.nome),
      fotos: jsonArr(r.fotos),
      marca_produto: str(r.marca_produto),
      categoria: str(r.categoria),
      grupo: (str(r.grupo) as Alimento["grupo"]) ?? null,
      sensorial: {
        textura: str(r.textura),
        temperatura: str(r.temperatura),
        cor: str(r.cor),
        forma_apresentacao: str(r.forma_apresentacao),
        cheiro: str(r.cheiro),
      },
      status: (str(r.status) ?? "nao_oferecido") as Alimento["status"],
      observacoes: str(r.observacoes),
      arquivado: Number(r.arquivado) === 1,
      criado_em: String(r.criado_em),
    }));

    const ofertas: Oferta[] = (existe("oferta") ? ler("SELECT * FROM oferta") : []).map(
      (r) => ({
        id: String(r.id),
        alimento_id: String(r.alimento_id),
        crianca_id: String(r.crianca_id),
        data_hora: String(r.data_hora),
        passo_escada: Number(r.passo_escada) as Oferta["passo_escada"],
        porcao_comida_pct: num(r.porcao_comida_pct),
        porcao_oferecida: str(r.porcao_oferecida),
        refeicao: (str(r.refeicao) as Oferta["refeicao"]) ?? null,
        local: str(r.local),
        quem_ofereceu: str(r.quem_ofereceu),
        quem_presente: jsonArr(r.quem_presente),
        humor_antes: (str(r.humor_antes) as Oferta["humor_antes"]) ?? null,
        origem: (str(r.origem) ?? "cuidador") as Oferta["origem"],
        confirmado: Number(r.confirmado) === 1,
        notas: str(r.notas),
        profissional_nome: str(r.profissional_nome),
        profissional_especialidade:
          (str(r.profissional_especialidade) as Oferta["profissional_especialidade"]) ??
          null,
      }),
    );

    const fotosRows = existe("foto") ? ler("SELECT * FROM foto") : [];
    const fotos = fotosRows.map((r) => ({
      id: String(r.id),
      blob: new Blob([r.blob as BlobPart]),
      criado_em: String(r.criado_em),
    }));

    const experiencias: ExperienciaMusical[] = (
      existe("experiencia") ? ler("SELECT * FROM experiencia") : []
    ).map((r) => ({
      id: String(r.id),
      nome: String(r.nome),
      tipo: (str(r.tipo) ?? "cancao") as ExperienciaMusical["tipo"],
      midias: jsonArr(r.midias),
      funcao: (str(r.funcao) as ExperienciaMusical["funcao"]) ?? null,
      sonoro: {
        andamento: (str(r.andamento) as ExperienciaMusical["sonoro"]["andamento"]) ?? null,
        intensidade:
          (str(r.intensidade) as ExperienciaMusical["sonoro"]["intensidade"]) ?? null,
        timbre: str(r.timbre),
        previsibilidade:
          (str(r.previsibilidade) as ExperienciaMusical["sonoro"]["previsibilidade"]) ??
          null,
        familiaridade:
          (str(r.familiaridade) as ExperienciaMusical["sonoro"]["familiaridade"]) ?? null,
      },
      modalidade: jsonArr(r.modalidade) as ExperienciaMusical["modalidade"],
      status: (str(r.status) ?? "nao_usada") as ExperienciaMusical["status"],
      observacoes: str(r.observacoes),
      arquivado: Number(r.arquivado) === 1,
      criado_em: String(r.criado_em),
    }));

    const momentos: MomentoMusical[] = (
      existe("momento") ? ler("SELECT * FROM momento") : []
    ).map((r) => ({
      id: String(r.id),
      experiencia_id: String(r.experiencia_id),
      crianca_id: String(r.crianca_id),
      data_hora: String(r.data_hora),
      passo_engajamento: Number(r.passo_engajamento) as MomentoMusical["passo_engajamento"],
      estado_antes: (str(r.estado_antes) as MomentoMusical["estado_antes"]) ?? null,
      estado_depois: (str(r.estado_depois) as MomentoMusical["estado_depois"]) ?? null,
      duracao_min: num(r.duracao_min),
      contexto: (str(r.contexto) as MomentoMusical["contexto"]) ?? null,
      com_quem: jsonArr(r.com_quem),
      origem: (str(r.origem) ?? "cuidador") as MomentoMusical["origem"],
      confirmado: Number(r.confirmado) === 1,
      notas: str(r.notas),
    }));

    const profissionais: Profissional[] = (
      existe("profissional") ? ler("SELECT * FROM profissional") : []
    ).map((r) => ({
      id: String(r.id),
      nome: String(r.nome),
      especialidade: (str(r.especialidade) ?? "outro") as Profissional["especialidade"],
      registro_classe: str(r.registro_classe),
      contato: str(r.contato),
      arquivado: Number(r.arquivado) === 1,
      criado_em: String(r.criado_em),
    }));

    const notas: NotaSessao[] = (existe("nota") ? ler("SELECT * FROM nota") : []).map(
      (r) => ({
        id: String(r.id),
        crianca_id: String(r.crianca_id),
        profissional_id: String(r.profissional_id),
        especialidade: (str(r.especialidade) ?? "outro") as NotaSessao["especialidade"],
        data_sessao: String(r.data_sessao),
        objetivo: String(r.objetivo ?? ""),
        alimento_id: str(r.alimento_id),
        degrau_alvo: r.degrau_alvo == null ? null : (Number(r.degrau_alvo) as NotaSessao["degrau_alvo"]),
        degrau_observado:
          r.degrau_observado == null ? null : (Number(r.degrau_observado) as NotaSessao["degrau_observado"]),
        atividades: jsonArr(r.atividades),
        resultados: str(r.resultados),
        orientacoes_casa: str(r.orientacoes_casa),
        orientacao_feita_em: str(r.orientacao_feita_em),
        observacoes: str(r.observacoes),
        anexos: (() => {
          try {
            return r.anexos ? (JSON.parse(String(r.anexos)) as AnexoClinico[]) : [];
          } catch {
            return [];
          }
        })(),
        oferta_id_gerada: str(r.oferta_id_gerada),
        estado: (str(r.estado) ?? "publicada") as NotaSessao["estado"],
        versao_de: str(r.versao_de),
        criado_em: String(r.criado_em),
      }),
    );

    const atividades: AtividadePsicomotora[] = (
      existe("atividade") ? ler("SELECT * FROM atividade") : []
    ).map((r) => ({
      id: String(r.id),
      nome: String(r.nome),
      tipo: (str(r.tipo) ?? "jogo") as AtividadePsicomotora["tipo"],
      midias: jsonArr(r.midias),
      psicomotor: {
        fator_alvo:
          (str(r.fator_alvo) as AtividadePsicomotora["psicomotor"]["fator_alvo"]) ?? null,
        sistema: jsonArr(r.sistema) as AtividadePsicomotora["psicomotor"]["sistema"],
        intensidade:
          (str(r.intensidade) as AtividadePsicomotora["psicomotor"]["intensidade"]) ?? null,
        previsibilidade:
          (str(r.previsibilidade) as AtividadePsicomotora["psicomotor"]["previsibilidade"]) ??
          null,
      },
      demanda_relacional:
        (str(r.demanda_relacional) as AtividadePsicomotora["demanda_relacional"]) ?? null,
      status: (str(r.status) ?? "nao_usada") as AtividadePsicomotora["status"],
      seguranca: str(r.seguranca),
      observacoes: str(r.observacoes),
      arquivado: Number(r.arquivado) === 1,
      criado_em: String(r.criado_em),
    }));

    const momentosPsm: MomentoPsicomotor[] = (
      existe("momento_psm") ? ler("SELECT * FROM momento_psm") : []
    ).map((r) => ({
      id: String(r.id),
      atividade_id: String(r.atividade_id),
      crianca_id: String(r.crianca_id),
      data_hora: String(r.data_hora),
      passo_participacao: Number(r.passo_participacao) as MomentoPsicomotor["passo_participacao"],
      nivel_ajuda: (str(r.nivel_ajuda) as MomentoPsicomotor["nivel_ajuda"]) ?? null,
      estado_antes: (str(r.estado_antes) as MomentoPsicomotor["estado_antes"]) ?? null,
      estado_depois: (str(r.estado_depois) as MomentoPsicomotor["estado_depois"]) ?? null,
      duracao_min: num(r.duracao_min),
      contexto: (str(r.contexto) as MomentoPsicomotor["contexto"]) ?? null,
      com_quem: jsonArr(r.com_quem),
      origem: (str(r.origem) ?? "cuidador") as MomentoPsicomotor["origem"],
      confirmado: Number(r.confirmado) === 1,
      notas: str(r.notas),
    }));

    const cfgRows = existe("config") ? ler("SELECT * FROM config LIMIT 1") : [];
    const cfgRow = cfgRows[0];

    await db.transaction(
      "rw",
      [db.crianca, db.alimento, db.oferta, db.foto, db.config, db.experiencia, db.momento, db.profissional, db.nota, db.atividade, db.momento_psm],
      async () => {
      if (criancas.length) await db.crianca.bulkPut(criancas);
      if (alimentos.length) await db.alimento.bulkPut(alimentos);
      if (ofertas.length) await db.oferta.bulkPut(ofertas);
      if (fotos.length) await db.foto.bulkPut(fotos);
      if (experiencias.length) await db.experiencia.bulkPut(experiencias);
      if (momentos.length) await db.momento.bulkPut(momentos);
      if (profissionais.length) await db.profissional.bulkPut(profissionais);
      if (notas.length) await db.nota.bulkPut(notas);
      if (atividades.length) await db.atividade.bulkPut(atividades);
      if (momentosPsm.length) await db.momento_psm.bulkPut(momentosPsm);
      if (cfgRow) {
        let qtd = CONFIG_PADRAO.escolha_qtd_por_refeicao;
        try {
          if (cfgRow.escolha_qtd_por_refeicao)
            qtd = JSON.parse(String(cfgRow.escolha_qtd_por_refeicao));
        } catch {
          /* mantém o padrão */
        }
        const config: Config & { id: string } = {
          id: "singleton",
          crianca_ativa_id: str(cfgRow.crianca_ativa_id),
          reexposicao_dias: Number(cfgRow.reexposicao_dias ?? CONFIG_PADRAO.reexposicao_dias),
          modo_escolha_por_semana: Number(
            cfgRow.modo_escolha_por_semana ?? CONFIG_PADRAO.modo_escolha_por_semana,
          ),
          escolha_qtd_por_refeicao: qtd,
          whatsapp_destino: str(cfgRow.whatsapp_destino),
          musica_rotina_dias: Number(
            cfgRow.musica_rotina_dias ?? CONFIG_PADRAO.musica_rotina_dias,
          ),
          musica_escolha_qtd: Number(
            cfgRow.musica_escolha_qtd ?? CONFIG_PADRAO.musica_escolha_qtd,
          ),
          elo_lembrete_dias: Number(
            cfgRow.elo_lembrete_dias ?? CONFIG_PADRAO.elo_lembrete_dias,
          ),
          gingado_rotina_dias: Number(
            cfgRow.gingado_rotina_dias ?? CONFIG_PADRAO.gingado_rotina_dias,
          ),
          gingado_escolha_qtd: Number(
            cfgRow.gingado_escolha_qtd ?? CONFIG_PADRAO.gingado_escolha_qtd,
          ),
          // Perfil profissional / Agenda (ADR-22): colunas podem não existir — caem no padrão.
          perfil_profissional: Boolean(cfgRow.perfil_profissional ?? CONFIG_PADRAO.perfil_profissional),
          agenda_ativa: Boolean(cfgRow.agenda_ativa ?? CONFIG_PADRAO.agenda_ativa),
          clinica_id: str(cfgRow.clinica_id),
          agenda_provedor: cfgRow.agenda_provedor === "google" ? "google" : "nativo",
        };
        await db.config.put(config);
      }
    });

    return {
      criancas: criancas.length,
      alimentos: alimentos.length,
      ofertas: ofertas.length,
      fotos: fotos.length,
      experiencias: experiencias.length,
      momentos: momentos.length,
      profissionais: profissionais.length,
      notas: notas.length,
      atividades: atividades.length,
      momentosPsm: momentosPsm.length,
    };
  } finally {
    sdb.close();
  }
}
